import React from 'react'
import { Alert, Icon, StackView } from '@planningcenter/tapestry-react'
import {
  Code,
  Link,
  List,
  PageHeading,
  Heading,
  Text,
} from '../../../shared/typography'

const page = () => {
  return (
    <StackView axis="vertical" spacing={2}>
      <PageHeading>Add-ons Component Library</PageHeading>

      <Text>
        Since your add-on cannot manipulate the DOM directly, nor can it render
        arbitrary HTML, we provide a{' '}
        <Link
          to="https://planningcenter.github.io/add-ons-js/?path=/docs/components-avatar--docs"
          external
        >
          component library
        </Link>{' '}
        that offers various User Interface controls and composable parts with
        which to build your add-on.
      </Text>

      <Alert
        stroke="primary-darkest"
        backgroundColor="primary-lightest"
        padding={2}
      >
        <StackView axis="horizontal" alignment="center" spacing={2}>
          <Icon
            name="general.outlinedGenericFile"
            color="primary-darkest"
            size="xl"
          />
          <Text>
            <Link
              to="https://planningcenter.github.io/add-ons-js/?path=/docs/components-avatar--docs"
              external
            >
              The component library is documented separately here.
            </Link>{' '}
          </Text>
        </StackView>
      </Alert>

      <Heading anchored id="use-uncontrolled-components" size={1} level={2}>
        Use Uncontrolled Components
      </Heading>

      <Text>
        All interactions with your add-on are serialized and sent "down" to the
        Web Worker, and there can be a noticeable input delay when using
        controlled components. It is recommended that you use "uncontrolled""
        components where possible to eliminate input lag. That means utilizing
        the <Code>defaultValue</Code> option for all components and updating
        your add-on state based on callbacks.
      </Text>

      <Heading anchored id="limitations" size={1} level={2}>
        Limitations of Add-ons
      </Heading>

      <Text>
        Since your add-on runs in a Web Worker apart from the main browser
        execution thread, there are limitations to what your add-on can do:
      </Text>

      <List>
        <List.Item>
          <Text>No access to the DOM</Text>
        </List.Item>
        <List.Item>
          <Text>
            No access to <Code>document</Code> or <Code>window</Code>
          </Text>
        </List.Item>
        <List.Item>
          <Text>
            No support for React <Code>ref</Code>
          </Text>
        </List.Item>
        <List.Item>
          <Text>Your add-on cannot control the version of React</Text>
        </List.Item>
        <List.Item>
          <Text>
            Components only accept basic data types that are serializable, e.g.
            strings, numbers, <Code>true</Code>, <Code>false</Code>,{' '}
            <Code>null</Code>, and objects/arrays that contain those basic
            types.
          </Text>
        </List.Item>
        <List.Item>
          <Text>
            Functions passed as arguments are converted to async functions.
          </Text>
        </List.Item>
        <List.Item>
          <Text>No support for React "render functions."</Text>
        </List.Item>
        <List.Item>
          <Text>No support for passing React components in props.</Text>
        </List.Item>
        <List.Item>
          <Text>
            Certain React libraries will not work within the above confines,
            such as routing libraries.
          </Text>
        </List.Item>
      </List>

      <Alert type="warning" padding={2}>
        <StackView axis="horizontal" alignment="center" spacing={2}>
          <Icon
            name="general.exclamationTriangle"
            color="warning-dark"
            size="xl"
          />
          <Text>
            <b>This section is limited and needs more info.</b> For now, the{' '}
            <Link
              to="https://docs.stripe.com/stripe-apps/how-ui-extensions-work#sandbox-limitations"
              external
            >
              limitations on Stripe UI Extensions documented here
            </Link>{' '}
            apply to our UI Extensions as well.
          </Text>
        </StackView>
      </Alert>
    </StackView>
  )
}

export default page
